import { BaseElement } from "../utils/ui-components";
import { customElement, property, state } from "lit/decorators.js";
import { html } from "lit";
import { playCircleIcon, pauseCircleIcon, closeIcon } from "../utils/icons";
import { MediaItem, MediaItemSegment } from "../data/types";
import { getSourcePath } from "../data/types";
import { getRelativeTimeString } from "../utils/utils";

export interface AudioState {
    item: MediaItem;
    segment?: MediaItemSegment;
    subtitle?: {
        text: string;
        start: number;
    };
}

@customElement("audio-player")
export class AudioPlayer extends BaseElement {
    @state()
    private currentAudio: AudioState | null = null;

    @state()
    private isPlaying = false;

    @state()
    private progress = 0;

    @state()
    private currentTime = 0;

    @state()
    private currentSubtitle: string | null = null;

    @state()
    private currentSegment: MediaItemSegment | null = null;

    private audio: HTMLAudioElement | null = null;
    private progressInterval: number | null = null;

    play(state: AudioState) {
        const startTime = (state.segment?.offset || 0) + (state.subtitle?.start || 0);

        if (this.audio && this.currentAudio?.item.id == state.item.id) {
            // Same audio file, just seek and update state
            this.currentAudio = state; // Update state even for same file
            this.seekTo(startTime);
        } else {
            // New audio file
            this.currentAudio = state;
            this.setupAudio(startTime);
        }
    }

    private async setupAudio(startMs: number) {
        await this.cleanup();

        if (!this.currentAudio) return;

        this.audio = new Audio(`/data/${this.currentAudio.item.id}.mp3`);
        await new Promise((resolve) => {
            this.audio!.addEventListener("loadedmetadata", resolve, { once: true });
        });

        this.audio.addEventListener("ended", this.handleEnded);
        await this.seekTo(startMs);
    }

    private async seekTo(timeMs: number) {
        if (!this.audio) return;

        this.audio.currentTime = timeMs / 1000;
        await this.audio.play();

        this.isPlaying = true;
        this.startProgressTracking();
    }

    private async cleanup() {
        if (this.audio) {
            try {
                await this.audio.pause();
                this.audio.removeEventListener("ended", this.handleEnded);
                this.audio = null;
            } catch (error) {
                console.error("Error during cleanup:", error);
            }
        }
        if (this.progressInterval) {
            window.clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
        this.isPlaying = false;
        this.progress = 0;
        this.currentTime = 0;
    }

    private handleEnded = () => {
        this.cleanup();
    };

    private startProgressTracking() {
        if (!this.audio) return;

        this.progressInterval = window.setInterval(() => {
            if (!this.audio) return;
            this.progress = (this.audio.currentTime / this.audio.duration) * 100;
            this.currentTime = this.audio.currentTime;

            // Update current segment and subtitle
            const currentTimeMs = this.audio.currentTime * 1000;

            // Find current segment
            if (this.currentAudio?.item.segments) {
                this.currentSegment =
                    this.currentAudio.item.segments.find((seg, i, arr) => {
                        const nextSeg = arr[i + 1];
                        return currentTimeMs >= seg.offset && (!nextSeg || currentTimeMs < nextSeg.offset);
                    }) || null;
            }

            // Find current subtitle within segment
            if (this.currentSegment?.subtitles) {
                const segmentOffset = this.currentSegment.offset;
                const relativeTime = currentTimeMs - segmentOffset;

                const subtitle = this.currentSegment.subtitles.find((sub, i, arr) => {
                    const nextSub = arr[i + 1];
                    return relativeTime >= sub.start && (!nextSub || relativeTime < nextSub.start);
                });

                this.currentSubtitle = subtitle?.text || null;
            } else {
                this.currentSubtitle = null;
            }
        }, 50);
    }

    private async togglePlayback() {
        if (!this.audio) return;

        if (this.isPlaying) {
            await this.audio.pause();
            this.isPlaying = false;
            if (this.progressInterval) {
                window.clearInterval(this.progressInterval);
                this.progressInterval = null;
            }
        } else {
            try {
                await this.audio.play();
                this.isPlaying = true;
                this.startProgressTracking();
            } catch (error) {
                console.error("Error during play:", error);
                await this.cleanup();
            }
        }
    }

    private handleSeekClick(e: MouseEvent) {
        if (!this.audio) return;

        const bar = e.currentTarget as HTMLDivElement;
        const rect = bar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        const newTime = (percentage / 100) * this.audio.duration;

        this.audio.currentTime = newTime;
        this.progress = percentage;
        this.currentTime = newTime;
    }

    private formatTime(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }

    private handleClose = async () => {
        await this.cleanup();
        this.currentAudio = null;
        this.currentSegment = null;
        this.currentSubtitle = null;
        // Dispatch the close event
        this.dispatchEvent(new CustomEvent("close"));
    };

    render() {
        if (!this.currentAudio) return null;

        return html`
            <div class="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t h-[120px]">
                <div class="h-full flex flex-col p-4">
                    <div class="max-w-[800px] mx-auto w-full flex-1 min-h-0 flex flex-col">
                        <div class="flex justify-between items-start mb-2 overflow-y-auto">
                            <a
                                href=${this.currentAudio.item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="flex items-start gap-3 min-w-0 flex-1 hover:text-blue-600 transition-colors group"
                            >
                                <img
                                    src="/img/${getSourcePath(this.currentAudio.item.path)}.jpg"
                                    alt=""
                                    class="w-8 h-8 rounded-md object-cover flex-shrink-0"
                                />
                                <div class="flex flex-col gap-1 min-w-0 overflow-hidden">
                                    <span class="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                                        ${this.currentAudio.item.title}
                                    </span>
                                    ${this.currentSegment
                                        ? html` <span class="text-sm text-gray-600 truncate">${this.currentSegment.title}</span> `
                                        : ""}
                                    ${this.currentSubtitle ? html` <span class="text-sm text-gray-600 truncate">${this.currentSubtitle}</span> ` : ""}
                                </div>
                                <span class="text-sm text-gray-400 flex-shrink-0 ml-auto">
                                    ${getRelativeTimeString(new Date(this.currentAudio.item.date))}
                                </span>
                            </a>
                            <button @click=${this.handleClose} class="p-1.5 rounded-full hover:bg-gray-100 ml-3 flex-shrink-0">
                                <i class="w-4 h-4 icon">${closeIcon}</i>
                            </button>
                        </div>
                        <div class="flex items-center gap-2 mt-auto">
                            <button @click=${this.togglePlayback} class="flex items-center justify-center hover:opacity-80">
                                <i class="w-8 h-8 icon">${this.isPlaying ? pauseCircleIcon : playCircleIcon}</i>
                            </button>
                            <span class="text-sm text-gray-500 w-12">${this.formatTime(this.currentTime)}</span>
                            <div class="h-1 bg-gray-200 rounded cursor-pointer relative flex-grow" @click=${this.handleSeekClick}>
                                <div class="absolute top-0 left-0 h-full bg-blue-500 rounded" style="width: ${this.progress}%"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
